/**
 * ניהול auth ב-localStorage — מונע מצב שבור: ערך המחרוזת "undefined", או app_user עם HTML במקום JSON
 * (קורה כשבקשת API מחזירה דף SPA במקום JSON ונשמר בטעות).
 */

const AUTH_TOKEN_KEY = "auth_token";
const APP_USER_KEY = "app_user";

export function readAuthToken() {
  if (typeof localStorage === "undefined") return null;
  const t = localStorage.getItem(AUTH_TOKEN_KEY);
  if (t == null || t === "" || t === "undefined" || t === "null") return null;
  return t;
}

/** שומר טוקן רק אם הוא מחרוזת תקינה; אחרת מסיר את המפתח */
export function writeAuthToken(token) {
  if (typeof localStorage === "undefined") return;
  if (!token || typeof token !== "string" || token === "undefined" || token === "null") {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return;
  }
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

/** מחזיר אובייקט משתמש או null; מנקה ערך פגום (HTML / לא JSON) */
export function readStoredUser() {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(APP_USER_KEY);
  if (!raw) return null;
  const trimmed = raw.trim();
  const head = trimmed.slice(0, 64).toLowerCase();
  if (head.startsWith("<!") || head.startsWith("<html") || head.includes("<!doctype")) {
    localStorage.removeItem(APP_USER_KEY);
    return null;
  }
  try {
    const u = JSON.parse(raw);
    if (u && typeof u === "object") return u;
  } catch {
    localStorage.removeItem(APP_USER_KEY);
  }
  return null;
}

export function clearAuthStorage() {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(APP_USER_KEY);
}

/** ניקוי חד-פעמי אם נשמרה מחרוזת "undefined" כטוקן */
export function sanitizeCorruptedAuthKeys() {
  if (typeof localStorage === "undefined") return;
  const t = localStorage.getItem(AUTH_TOKEN_KEY);
  if (t === "undefined" || t === "null" || t === "") {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
  readStoredUser(); // מסיר app_user אם HTML
}

/**
 * מחלץ token + user מתשובת POST /auth/verify-otp (גם אם עטופה ב־data / payload, או accessToken).
 * reason: למה נכשל — html | no_token | invalid_json | empty | not_object
 */
export function parseVerifyOtpResponse(body) {
  if (body == null) {
    return { token: null, user: null, reason: "empty" };
  }
  if (typeof body === "string") {
    const s = body.trim();
    if (!s) return { token: null, user: null, reason: "empty" };
    const head = s.slice(0, 80).toLowerCase();
    if (s.startsWith("<") || head.includes("<!doctype") || head.startsWith("<html")) {
      return { token: null, user: null, reason: "html" };
    }
    try {
      return parseVerifyOtpResponse(JSON.parse(s));
    } catch {
      return { token: null, user: null, reason: "invalid_json" };
    }
  }
  if (typeof body !== "object" || Array.isArray(body)) {
    return { token: null, user: null, reason: "not_object" };
  }

  const inner = unwrapVerifyOtpPayload(body);
  const token = pickJwtString(inner);
  const user = inner?.user ?? inner?.profile ?? inner?.account ?? null;

  if (!token) {
    return { token: null, user, reason: "no_token" };
  }
  return { token, user, reason: null };
}

function unwrapVerifyOtpPayload(raw) {
  const candidates = [raw.data, raw.payload, raw.result];
  for (const c of candidates) {
    if (
      c &&
      typeof c === "object" &&
      !Array.isArray(c) &&
      (c.token != null || c.accessToken != null || c.access_token != null || c.user != null)
    ) {
      return c;
    }
  }
  return raw;
}

function pickJwtString(obj) {
  if (!obj || typeof obj !== "object") return null;
  const t = obj.token ?? obj.accessToken ?? obj.access_token;
  if (typeof t !== "string") return null;
  const s = t.trim();
  if (!s || s === "undefined" || s === "null") return null;
  return s;
}
